import { useInjection } from 'inversify-react';
import { IOsiPaymentsWidgetViewModel, IOsiPaymentsWidgetViewModelToken } from '@shared/types/mobx/widgets/OsiPayments';
import { observer } from 'mobx-react-lite';
import { tokens, useTranslation } from '@shared/utils/i18n';
import { useMemo } from 'react';
import { flatComparator } from '@shared/utils/helpers/flatComparator';
import { Box, Button, Grid, TextField } from '@mui/material';
import DatePicker from '@mui/lab/DatePicker';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import {
  DataGridPro,
  GridColDef,
  GridValueGetterParams,
  useGridApiRef,
  gridVisibleSortedRowIdsSelector
} from '@mui/x-data-grid-pro';
import DataGridToolBar from '@shared/common/DataGridToolBar';
import { OsiCreatePaymentButton } from '@features/osi/payments/createPayment';
import { Trans } from 'react-i18next';

export const OsiPaymentsWidget: React.FC = observer(() => {
  const vm = useInjection<IOsiPaymentsWidgetViewModel>(IOsiPaymentsWidgetViewModelToken);
  const { translateToken: tt, getGridLocale, t } = useTranslation();
  // OSI-808: apiRef нужен, чтобы на «Скачать» достать id строк, реально видимых в гриде
  // (с учётом фильтра и сортировки) — без apiRef фильтр DataGridPro наружу не выходит.
  const apiRef = useGridApiRef();

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'dt',
        headerName: tt(tokens.osiPayments.table.date),
        minWidth: 100,
        type: 'date',
        flex: 0.2,
        valueFormatter: (params) => new Date(params.value).toLocaleString('ru-RU')
      },
      { field: 'bankName', headerName: tt(tokens.osiPayments.table.acceptedBank), minWidth: 100, flex: 0.2 },
      {
        field: 'abonentName',
        headerName: tt(tokens.osiPayments.table.abonentName),
        minWidth: 150,
        flex: 0.3
      },
      {
        field: 'flat',
        headerName: tt(tokens.osiPayments.table.number),
        sortComparator: (v1, v2) => flatComparator(v1, v2),
        minWidth: 100,
        flex: 0.2,
        // Фаза 2: показываем flatDisplay (единый номер помещения от backend). DTO отчёта не содержит
        // areaTypeCode, но backend гарантирует flatDisplay == сырому flat для не-арендаторов, поэтому
        // безусловный fallback безопасен (префиксы makeFlat в этой колонке никогда не применялись).
        valueGetter: (params: GridValueGetterParams) => params.row.flatDisplay ?? params.row.flat
      },
      { field: 'serviceName', headerName: tt(tokens.osiPayments.table.service), minWidth: 150, flex: 0.3 },
      {
        field: 'amount',
        headerName: tt(tokens.osiPayments.table.amount),
        minWidth: 150,
        flex: 0.2,
        align: 'left',
        type: 'number',
        valueGetter: (params) => params.value.toFixed(2)
      }
    ],
    [tt]
  );

  return (
    <>
      <Box>
        <OsiCreatePaymentButton onSuccess={vm.loadPayments} />
      </Box>
      <Box display="flex" alignItems="left">
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <DatePicker
              views={['year', 'month']}
              openTo={'month'}
              disabled={vm.isLoading}
              onAccept={(date: Date | null) => {
                vm.dateBegin = date;
              }}
              label={tt(tokens.common.formFields.fromMonth.label)}
              minDate={new Date('2021-03-01')}
              maxDate={new Date('2031-03-01')}
              value={vm.dateBegin}
              onChange={(newValue) => null}
              renderInput={(params) => <TextField size="small" {...params} helperText={null} sx={{ width: '100%' }} />}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <DatePicker
              views={['year', 'month']}
              openTo={'month'}
              disabled={vm.isLoading}
              onAccept={(date: Date | null) => {
                vm.dateEnd = date;
              }}
              label={tt(tokens.common.formFields.toMonth.label)}
              minDate={new Date('2021-03-01')}
              maxDate={new Date('2031-03-01')}
              value={vm.dateEnd}
              onChange={(newValue) => null}
              renderInput={(params) => <TextField size="small" {...params} helperText={null} sx={{ width: '100%' }} />}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <Button
              fullWidth
              disabled={vm.isLoading}
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={async () => {
                await vm.loadPayments();
              }}
              size="medium"
            >
              <Trans i18nKey={'common:refresh'} />
            </Button>
          </Grid>
          <Grid item xs={6} md={2}>
            <Button
              disabled={vm.isLoading}
              fullWidth
              variant="outlined"
              color="primary"
              startIcon={<CloudDownloadIcon />}
              onClick={async () => {
                // OSI-808: gridVisibleSortedRowIdsSelector — тот же штатный селектор MUI, что уже
                // используется в OsvGrid для получения строк с учётом фильтра/сортировки грида
                // (apiRef.current.state). Без фильтра он вернёт id всех строк по порядку.
                const visibleRowIds = gridVisibleSortedRowIdsSelector(apiRef.current.state).map(Number);
                await vm.downloadReport(visibleRowIds);
              }}
              size="medium"
            >
              <Trans i18nKey={'common:download'} />
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ height: '62vh' }}>
        <DataGridPro
          apiRef={apiRef}
          loading={vm.isLoading}
          localeText={getGridLocale()}
          rows={vm.rows}
          columns={columns}
          density="compact"
          disableSelectionOnClick
          components={{
            Toolbar: DataGridToolBar
          }}
        />
      </Box>
    </>
  );
});
